// 🔒 ECF-DUPLICADOS-2026-09-14 — Panel de doble emisión y e-NCF huérfanos.
// Existe porque 12 facturas de La Terraza se emitieron por duplicado entre el
// 12 y el 14-09 y nadie lo vio en tres días: el dashboard muestra el e-NCF que
// la factura conservó, y el huérfano no aparecía en ninguna pantalla.
import { useEffect, useState } from 'react';
import { AlertTriangle, Copy, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { notify } from '@/lib/notify';

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('pos_token')}` });

export default function EcfDuplicatesPanel({ days = 7 }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/ecf/duplicates?days=${days}`, { headers: hdrs() });
      if (r.ok) setData(await r.json());
    } catch { /* el panel nunca rompe el dashboard */ }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (!data || (data.total_doble_emision === 0 && data.total_huerfanos === 0)) return null;

  const vivos = data.encf_huerfanos.filter((h) => h.vivo_en_dgii);
  const copiar = () => {
    const txt = data.encf_huerfanos
      .map((h) => `${h.encf}\tT-${h.transaction_number ?? '?'}\t${h.ecf_type || ''}\tRD$ ${h.total ?? ''}\t${h.estado}\t${(h.emitido_at || '').slice(0, 19).replace('T', ' ')}`)
      .join('\n');
    navigator.clipboard.writeText(`e-NCF\tTrans\tTipo\tMonto\tEstado DGII\tEmitido\n${txt}`)
      .then(() => notify.success('Lista copiada — pegala en Excel o mandala a tu contadora'))
      .catch(() => notify.error('No se pudo copiar'));
  };

  return (
    <div className="border border-red-500/50 bg-red-500/5 rounded-xl p-4 mb-4" data-testid="ecf-duplicates-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle size={16} className="text-red-500 shrink-0" />
          <h3 className="text-sm font-bold text-red-600 dark:text-red-400" data-testid="ecf-duplicates-title">
            {data.total_doble_emision > 0
              ? `${data.total_doble_emision} factura(s) con más de un e-NCF emitido`
              : `${data.total_huerfanos} e-NCF sin venta detrás`}
          </h3>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={load} className="p-1.5 rounded-lg hover:bg-red-500/10" title="Volver a revisar" data-testid="ecf-duplicates-refresh">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={copiar} className="p-1.5 rounded-lg hover:bg-red-500/10" title="Copiar la lista" data-testid="ecf-duplicates-copy">
            <Copy size={14} />
          </button>
          <button onClick={() => setOpen(!open)} className="p-1.5 rounded-lg hover:bg-red-500/10" data-testid="ecf-duplicates-toggle">
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mt-2" data-testid="ecf-duplicates-summary">
        Últimos {data.dias} días · <b className="text-red-600 dark:text-red-400">{vivos.length}</b> e-NCF
        huérfano(s) que la DGII ya aceptó o tiene en cola{data.total_huerfanos > vivos.length
          ? ` · ${data.total_huerfanos - vivos.length} más que quedaron rechazados o sin llegar (esos no hay que anularlos)`
          : ''}.
        La anulación es decisión de tu contadora — acá solo te los mostramos.
      </p>

      {open && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground border-b border-red-500/20">
                <th className="text-left p-2">e-NCF huérfano</th>
                <th className="text-left p-2">Factura</th>
                <th className="text-left p-2">Tipo</th>
                <th className="text-right p-2">Monto</th>
                <th className="text-left p-2">Estado DGII</th>
                <th className="text-left p-2">Emitido</th>
                <th className="text-left p-2">e-NCF que quedó en la factura</th>
              </tr>
            </thead>
            <tbody>
              {data.encf_huerfanos.map((h) => {
                const f = data.facturas_con_doble_emision.find((x) => x.bill_id === h.bill_id);
                return (
                  <tr key={h.encf} className="border-b border-red-500/10" data-testid={`ecf-orphan-row-${h.encf}`}>
                    <td className="p-2 font-mono font-bold">{h.encf}</td>
                    <td className="p-2">T-{h.transaction_number ?? '?'}</td>
                    <td className="p-2">{h.ecf_type || '—'}</td>
                    <td className="p-2 text-right">{h.total != null ? `RD$ ${Number(h.total).toFixed(2)}` : '—'}</td>
                    <td className={`p-2 font-bold ${h.vivo_en_dgii ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                      {h.estado || '—'}{h.vivo_en_dgii ? ' · anular' : ''}
                    </td>
                    <td className="p-2">{(h.emitido_at || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="p-2 font-mono">{f?.encf_vigente || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
